import { IAddress } from '@app/models/Address'
import { IAssociatedContact } from '@app/models/AssociatedContact'
import { IAssociatedListing } from '@app/models/AssociatedListing'
import {
  ListingType,
  PriceUnits,
  PropertyType,
  RentalRateUnits,
} from '@app/models/enums'
import { IListing } from '@app/models/Listing'
import { IProperty } from '@app/models/Property'
import { List } from 'immutable'
import moment from 'moment'

export function formatAddressSingleLine(address: IAddress) {
  const output: string[] = []

  if (address.line1) {
    output.push(address.line1)
  }

  if (address.city) {
    output.push(address.city)
  }

  if (address.state) {
    output.push(address.state)
  }

  if (address.zip) {
    output.push(address.zip)
  }

  return output.join(', ')
}

export function formatBoolean(
  value: boolean,
  trueText: string = 'Yes',
  falseText = 'No',
) {
  return value === true ? trueText : falseText
}

export function formatContactNameList(
  contacts: List<IAssociatedContact>,
): string {
  if (contacts.size === 0) {
    return ''
  } else if (contacts.size === 1) {
    return contacts.first().name
  } else {
    return `${contacts.first().name} + ${contacts.size - 1} more`
  }
}

export function formatCurrency(value: number, fractionDigits: number = 0) {
  return `$${formatNumber(value, fractionDigits)}`
}

export function formatDate(
  date: number | Date | undefined,
  format?: string,
  noDateValue?: string,
) {
  if (date === undefined) {
    return noDateValue || ''
  } else if (typeof date === 'number') {
    return moment.unix(date).format(format)
  } else {
    return moment(date).format(format)
  }
}

export function formatDateFromNow(
  date: number | Date | undefined,
  noDateValue?: string,
): string {
  if (date === undefined) {
    return noDateValue || ''
  } else if (typeof date === 'number') {
    return moment.unix(date).fromNow()
  } else {
    return moment(date).fromNow()
  }
}

export function formatKeyToReadableDate(value: string, delimiter: string = '') {
  // value is in format 20187, for example
  // if delimiter specified, looking for 7/2018, for example
  if (!value) {
    return ''
  }

  let year = value.substring(0, 4)
  let month = value.substring(4)

  if (delimiter !== '') {
    month = value.substring(0, value.indexOf(delimiter))
    year = value.substring(value.indexOf(delimiter) + 1)
  }
  return moment(
    `${year}-${month.length === 1 ? '0' + month : month}-01`,
  ).format('MMMM YYYY')
}

export function formatListingPrice(
  listing: IListing | IAssociatedListing,
): string {
  if (listing.type === ListingType.Lease && listing.leaseData) {
    const roundedTo = listing.leaseData.rentalRate.rate % 1 !== 0 ? 2 : 0
    return `${formatCurrency(
      listing.leaseData.rentalRate.rate,
      roundedTo,
    )} ${formatPriceUnit(listing.leaseData.rentalRate.units)}`
  } else if (listing.saleData) {
    if (listing.saleData.askingPrice.confidential) {
      return 'Contact Broker for Price'
    }

    const roundedTo = listing.saleData.askingPrice.amount % 1 !== 0 ? 2 : 0
    return `${formatCurrency(
      listing.saleData.askingPrice.amount,
      roundedTo,
    )} ${formatPriceUnit(listing.saleData.askingPrice.units)}`.trim()
  } else {
    return ''
  }
}

export function formatListingSize(listing: IListing): string {
  if (listing.lot) {
    return listing.lot.lotSize.toString()
  } else if (listing.space) {
    return listing.space.spaceAvailable.toString()
  } else {
    return formatPropertySize(listing.property)
  }
}

export function formatNumber(value: number, fractionDigits: number = 0) {
  return value.toLocaleString(navigator.language, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  })
}

export function formatPhoneNumber(value: string) {
  value = value || ''

  if (value.length === 10) {
    return (
      '(' +
      value.substring(0, 3) +
      ') ' +
      value.substring(3, 6) +
      '-' +
      value.substring(6, 10)
    )
  } else if (value.length > 10) {
    return (
      '(' +
      value.substring(0, 3) +
      ') ' +
      value.substring(3, 6) +
      '-' +
      value.substring(6, 10) +
      ' ext. ' +
      value.substring(10, value.length)
    )
  } else {
    return value
  }
}

export function formatPriceUnit(unit: PriceUnits | RentalRateUnits): string {
  if (unit === PriceUnits.Total) {
    return ''
  }
  return unit.replace('Price', '').replace(/Per/g, ' / ')
}

export function formatPropertySize(property: IProperty): string {
  if (
    property.propertyTypes.includes(PropertyType.AgriculturalLand) ||
    property.propertyTypes.includes(PropertyType.Land) ||
    property.propertyTypes.contains(PropertyType.RecreationalLand)
  ) {
    return property.totalLotSize.toString()
  } else {
    return `${formatNumber(property.totalBuildingSize)} SF`
  }
}

export function formatUnitAbbreviation(unit: string): string {
  unit = unit || ''
  return unit.replace('SqFt', 'SF').replace('Acres', 'AC').replace('Acre', 'AC')
}

export function humanize(input?: string): string {
  if (!input) {
    return ''
  }

  const wordPattern = /([A-Z][^A-Z]+)|([A-Z]+(?![^A-Z]))/g
  const matches = input.match(wordPattern)
  return matches ? matches.join(' ').replace('_', '') : input.replace('_', '')
}
